import { Injectable } from '@angular/core';
import { faTag } from '@fortawesome/pro-duotone-svg-icons';
import { ImmutableContext } from '@ngxs-labs/immer-adapter';
import { Action, Actions, Selector, State, StateContext, Store } from '@ngxs/store';
import { Observable } from 'rxjs';
import { EditorType } from 'src/app/samwebDescription/samweb/editorType';
import { PresentationType } from 'src/app/samwebDescription/samweb/settings/presentationType';
import { BrickSettingsService, SingleCallTagEditorBrickSettingRemoteDto } from 'src/generated/swagger';
import { IBrickSettingStateModel, SpecificBrickSettingsStateBase } from '../specific-brick-settings-base';
import { TabBrickSettingsStateModel } from '../tab-brick-settings/tab-brick-settings.state';
import {
   ChangeCallTagDisplayNameValueForSingleCallTagBrickSettings,
   ChangeCallTagIdForSingleCallTagBrickSettings,
   ChangeCallTagSourceActiveCallForSingleCallTagBrickSettings,
   ChangeCallTagSourceCallHistoryForSingleCallTagBrickSettings,
   ChangeClearTagValueForSingleCallTagBrickSettings,
   ChangeEditBackgroundColorForSingleCallTagBrickSettings,
   ChangeEditFontColorForSingleCallTagBrickSettings,
   ChangeIsReadOnlyForSingleCallTagBrickSettings,
   ChangeShowDescriptionTextAsPopoverForSingleCallTagBrickSettings,
   ChangeTagEditorDescriptionTextForSingleCallTagBrickSettings,
   ChangeTagEditorPresentationTypeForSingleCallTagBrickSettings,
   ChangeTagEditorSettingsForSingleCallTagBrickSettings,
   ChangeTagEditorTypeForSingleCallTagBrickSettings,
   ChangeTagIconColorForSingleCallTagBrickSettings,
   ChangeTagIconForSingleCallTagBrickSettings,
} from './single-call-tag-brick-settings.actions';

export interface SingleCallTagBrickSettingsStateModel extends IBrickSettingStateModel<SingleCallTagEditorBrickSettingRemoteDto> {
   isBrickDefinitionMenuOpen: boolean;
}

function getDefaultSpecificBrickSetting(): SingleCallTagEditorBrickSettingRemoteDto {
   return {
      commonBrickSettingRemoteDto: null,
      callTagId: 'MEMO',
      readOnlyMode: false,
      tagEditFontColor: null,
      tagEditBackgroundColor: null,
      tagIcon: faTag.iconName,
      tagIconColor: null,
      presentationType: PresentationType.ICON_AND_TEXT.value,
      clearTagValue: '',
      descriptionText: '',
      showDescriptionTextAsPopover: false,
      showCallTagsForHistoryCalls: true,
      showCallTagsForActiveCall: true,
      callTagDisplayName: null,
      editorSettings: {
         editorType: EditorType.TEXT_EDITOR.value,
      },
   };
}

@State<SingleCallTagBrickSettingsStateModel>({
   name: 'singleCallTagEditorBrickSettings',
   defaults: {
      specificBrickSettings: {
         brickSettings: {},
         brickSettingsEditMode: null,
      },
      isBrickDefinitionMenuOpen: false,
   },
})
@Injectable()
export class SingleCallTagBrickSettingsState extends SpecificBrickSettingsStateBase<SingleCallTagEditorBrickSettingRemoteDto>(getDefaultSpecificBrickSetting) {
   constructor(private brickSettingsService: BrickSettingsService, store: Store, actions$: Actions) {
      super(store);
   }

   @Selector([SingleCallTagBrickSettingsState, SingleCallTagBrickSettingsState.getSettingModelFn])
   public static getSettings(
      state,
      getSettingModelFn: (state, brickInstanceId) => SingleCallTagEditorBrickSettingRemoteDto
   ): (brickInstanceId: string) => SingleCallTagEditorBrickSettingRemoteDto {
      return (brickInstanceId: string) => {
         return getSettingModelFn(state, brickInstanceId);
      };
   }

   @Selector([SingleCallTagBrickSettingsState.getSettings])
   public static isReadOnly(getSettings: (brickInstanceId: string) => SingleCallTagEditorBrickSettingRemoteDto) {
      return (brickInstanceId: string) => {
         return getSettings(brickInstanceId)?.readOnlyMode ?? false;
      };
   }

   @Selector([SingleCallTagBrickSettingsState.getSettings])
   public static isShowCallTagsForActiveCall(getSettings: (brickInstanceId: string) => SingleCallTagEditorBrickSettingRemoteDto) {
      return (brickInstanceId: string) => {
         return getSettings(brickInstanceId)?.showCallTagsForActiveCall ?? false;
      };
   }

   @Selector([SingleCallTagBrickSettingsState.getSettings])
   public static isShowCallTagsForHistoryCalls(getSettings: (brickInstanceId: string) => SingleCallTagEditorBrickSettingRemoteDto) {
      return (brickInstanceId: string) => {
         return getSettings(brickInstanceId)?.showCallTagsForHistoryCalls ?? false;
      };
   }

   @Selector([SingleCallTagBrickSettingsState.getSettings])
   public static getCallTagId(getSettings: (brickInstanceId: string) => SingleCallTagEditorBrickSettingRemoteDto) {
      return (brickInstanceId: string) => {
         return getSettings(brickInstanceId)?.callTagId ?? '';
      };
   }

   @Selector([SingleCallTagBrickSettingsState.getSettings])
   public static getClearTag(getSettings: (brickInstanceId: string) => SingleCallTagEditorBrickSettingRemoteDto) {
      return (brickInstanceId: string) => {
         return getSettings(brickInstanceId)?.clearTagValue ?? '';
      };
   }

   @Selector([SingleCallTagBrickSettingsState.getSettings])
   public static getDescriptionText(getSettings: (brickInstanceId: string) => SingleCallTagEditorBrickSettingRemoteDto): (brickInstanceId: string) => string {
      return (brickInstanceId: string) => {
         return getSettings(brickInstanceId).descriptionText;
      };
   }

   @Selector([SingleCallTagBrickSettingsState.getSettings])
   public static getEditorType(getSettings: (brickInstanceId: string) => SingleCallTagEditorBrickSettingRemoteDto): (brickInstanceId: string) => EditorType {
      return (brickInstanceId: string) => {
         return EditorType.valueOf(getSettings(brickInstanceId).editorSettings.editorType);
      };
   }

   @Selector([SingleCallTagBrickSettingsState.getClearTag, SingleCallTagBrickSettingsState.getEditorType])
   public static getClearTagValue(getClearTag: (brickInstanceId: string) => string, getEditorType: (brickInstanceId: string) => EditorType) {
      return (brickInstanceId: string) => {
         return getClearTag(brickInstanceId);
      };
   }

   @Selector([SingleCallTagBrickSettingsState.getSettings])
   public static getTagPresentationType(
      getSettings: (brickInstanceId: string) => SingleCallTagEditorBrickSettingRemoteDto
   ): (brickInstanceId: string) => PresentationType {
      return (brickInstanceId: string) => {
         return PresentationType.valueOf(getSettings(brickInstanceId)?.presentationType);
      };
   }

   @Action(ChangeCallTagIdForSingleCallTagBrickSettings)
   @ImmutableContext()
   public changeCallTagIdForSingleCallTagBrickSettings(ctx: StateContext<TabBrickSettingsStateModel>, action: ChangeCallTagIdForSingleCallTagBrickSettings) {
      ctx.setState((state) => {
         const brickSettingModel = SingleCallTagBrickSettingsState.getSettingModelOrDefault(state, action.brickInstanceId);
         brickSettingModel.callTagId = action.callTagId;
         state.specificBrickSettings.brickSettingsEditMode[action.brickInstanceId] = brickSettingModel;
         return state;
      });
   }

   @Action(ChangeIsReadOnlyForSingleCallTagBrickSettings)
   @ImmutableContext()
   public changeIsReadOnlyForSingleCallTagBrickSettings(ctx: StateContext<TabBrickSettingsStateModel>, action: ChangeIsReadOnlyForSingleCallTagBrickSettings) {
      ctx.setState((state) => {
         const brickSettingModel = SingleCallTagBrickSettingsState.getSettingModelOrDefault(state, action.brickInstanceId);
         brickSettingModel.readOnlyMode = action.isReadOnly;
         state.specificBrickSettings.brickSettingsEditMode[action.brickInstanceId] = brickSettingModel;
         return state;
      });
   }

   @Action(ChangeEditBackgroundColorForSingleCallTagBrickSettings)
   @ImmutableContext()
   public changeEditBackgroundColor(ctx: StateContext<TabBrickSettingsStateModel>, action: ChangeEditBackgroundColorForSingleCallTagBrickSettings) {
      ctx.setState((state) => {
         const brickSettingModel = SingleCallTagBrickSettingsState.getSettingModelOrDefault(state, action.brickInstanceId);
         brickSettingModel.tagEditBackgroundColor = action.backgroundColor;
         state.specificBrickSettings.brickSettingsEditMode[action.brickInstanceId] = brickSettingModel;
         return state;
      });
   }

   @Action(ChangeEditFontColorForSingleCallTagBrickSettings)
   @ImmutableContext()
   public changeEditFontColor(ctx: StateContext<TabBrickSettingsStateModel>, action: ChangeEditFontColorForSingleCallTagBrickSettings) {
      ctx.setState((state) => {
         const brickSettingModel = SingleCallTagBrickSettingsState.getSettingModelOrDefault(state, action.brickInstanceId);
         brickSettingModel.tagEditFontColor = action.fontColor;
         state.specificBrickSettings.brickSettingsEditMode[action.brickInstanceId] = brickSettingModel;
         return state;
      });
   }

   @Action(ChangeTagIconForSingleCallTagBrickSettings)
   @ImmutableContext()
   public changeTagIcon(ctx: StateContext<TabBrickSettingsStateModel>, action: ChangeTagIconForSingleCallTagBrickSettings) {
      ctx.setState((state) => {
         const brickSettingModel = SingleCallTagBrickSettingsState.getSettingModelOrDefault(state, action.brickInstanceId);
         brickSettingModel.tagIcon = action.tagIcon.iconName;
         state.specificBrickSettings.brickSettingsEditMode[action.brickInstanceId] = brickSettingModel;
         return state;
      });
   }

   @Action(ChangeTagIconColorForSingleCallTagBrickSettings)
   @ImmutableContext()
   public changeTagIconColor(ctx: StateContext<TabBrickSettingsStateModel>, action: ChangeTagIconColorForSingleCallTagBrickSettings) {
      ctx.setState((state) => {
         const brickSettingModel = SingleCallTagBrickSettingsState.getSettingModelOrDefault(state, action.brickInstanceId);
         brickSettingModel.tagIconColor = action.iconColor;
         state.specificBrickSettings.brickSettingsEditMode[action.brickInstanceId] = brickSettingModel;
         return state;
      });
   }

   @Action(ChangeTagEditorTypeForSingleCallTagBrickSettings)
   @ImmutableContext()
   public changeTagEditorType(ctx: StateContext<TabBrickSettingsStateModel>, action: ChangeTagEditorTypeForSingleCallTagBrickSettings) {
      ctx.setState((state) => {
         const brickSettingModel = SingleCallTagBrickSettingsState.getSettingModelOrDefault(state, action.brickInstanceId);
         brickSettingModel.editorSettings.editorType = action.editorType.value;
         state.specificBrickSettings.brickSettingsEditMode[action.brickInstanceId] = brickSettingModel;
         return state;
      });
   }

   @Action(ChangeTagEditorPresentationTypeForSingleCallTagBrickSettings)
   @ImmutableContext()
   public ChangeTagEditorPresentationType(ctx: StateContext<TabBrickSettingsStateModel>, action: ChangeTagEditorPresentationTypeForSingleCallTagBrickSettings) {
      ctx.setState((state) => {
         const brickSettingModel = SingleCallTagBrickSettingsState.getSettingModelOrDefault(state, action.brickInstanceId);
         brickSettingModel.presentationType = action.presentationType.value;
         state.specificBrickSettings.brickSettingsEditMode[action.brickInstanceId] = brickSettingModel;
         return state;
      });
   }

   @Action(ChangeTagEditorDescriptionTextForSingleCallTagBrickSettings)
   @ImmutableContext()
   public ChangeTagEditorDescriptionText(ctx: StateContext<TabBrickSettingsStateModel>, action: ChangeTagEditorDescriptionTextForSingleCallTagBrickSettings) {
      ctx.setState((state) => {
         const brickSettingModel = SingleCallTagBrickSettingsState.getSettingModelOrDefault(state, action.brickInstanceId);
         brickSettingModel.descriptionText = action.descriptionText;
         state.specificBrickSettings.brickSettingsEditMode[action.brickInstanceId] = brickSettingModel;
         return state;
      });
   }

   @Action(ChangeShowDescriptionTextAsPopoverForSingleCallTagBrickSettings)
   @ImmutableContext()
   public ChangeShowDescriptionTextAsPopover(
      ctx: StateContext<TabBrickSettingsStateModel>,
      action: ChangeShowDescriptionTextAsPopoverForSingleCallTagBrickSettings
   ) {
      ctx.setState((state) => {
         const brickSettingModel = SingleCallTagBrickSettingsState.getSettingModelOrDefault(state, action.brickInstanceId);
         brickSettingModel.showDescriptionTextAsPopover = action.showDescriptionTextAsPopover;
         state.specificBrickSettings.brickSettingsEditMode[action.brickInstanceId] = brickSettingModel;
         return state;
      });
   }

   @Action(ChangeClearTagValueForSingleCallTagBrickSettings)
   @ImmutableContext()
   public ChangeClearTagValue(ctx: StateContext<TabBrickSettingsStateModel>, action: ChangeClearTagValueForSingleCallTagBrickSettings) {
      ctx.setState((state) => {
         const brickSettingModel = SingleCallTagBrickSettingsState.getSettingModelOrDefault(state, action.brickInstanceId);
         brickSettingModel.clearTagValue = action.clearTagValue;
         state.specificBrickSettings.brickSettingsEditMode[action.brickInstanceId] = brickSettingModel;
         return state;
      });
   }

   @Action(ChangeTagEditorSettingsForSingleCallTagBrickSettings)
   @ImmutableContext()
   public changeTagEditorSettings(ctx: StateContext<TabBrickSettingsStateModel>, action: ChangeTagEditorSettingsForSingleCallTagBrickSettings) {
      ctx.setState((state) => {
         const brickSettingModel = SingleCallTagBrickSettingsState.getSettingModelOrDefault(state, action.brickInstanceId);
         brickSettingModel.editorSettings = action.editorSettings;
         state.specificBrickSettings.brickSettingsEditMode[action.brickInstanceId] = brickSettingModel;
         return state;
      });
   }

   @Action(ChangeCallTagSourceActiveCallForSingleCallTagBrickSettings)
   @ImmutableContext()
   public changeCallTagSourceActiveCall(ctx: StateContext<TabBrickSettingsStateModel>, action: ChangeCallTagSourceActiveCallForSingleCallTagBrickSettings) {
      ctx.setState((state) => {
         const brickSettingModel = SingleCallTagBrickSettingsState.getSettingModelOrDefault(state, action.brickInstanceId);
         brickSettingModel.showCallTagsForActiveCall = action.isEnabled;
         state.specificBrickSettings.brickSettingsEditMode[action.brickInstanceId] = brickSettingModel;
         return state;
      });
   }

   @Action(ChangeCallTagSourceCallHistoryForSingleCallTagBrickSettings)
   @ImmutableContext()
   public changeCallTagSourceCallHistory(ctx: StateContext<TabBrickSettingsStateModel>, action: ChangeCallTagSourceCallHistoryForSingleCallTagBrickSettings) {
      ctx.setState((state) => {
         const brickSettingModel = SingleCallTagBrickSettingsState.getSettingModelOrDefault(state, action.brickInstanceId);
         brickSettingModel.showCallTagsForHistoryCalls = action.isEnabled;
         state.specificBrickSettings.brickSettingsEditMode[action.brickInstanceId] = brickSettingModel;
         return state;
      });
   }

   @Action(ChangeCallTagDisplayNameValueForSingleCallTagBrickSettings)
   @ImmutableContext()
   public ChangeCallTagDisplayNameV(ctx: StateContext<TabBrickSettingsStateModel>, action: ChangeCallTagDisplayNameValueForSingleCallTagBrickSettings) {
      ctx.setState((state) => {
         const brickSettingModel = SingleCallTagBrickSettingsState.getSettingModelOrDefault(state, action.brickInstanceId);
         brickSettingModel.callTagDisplayName = action.tagDisplayName;
         state.specificBrickSettings.brickSettingsEditMode[action.brickInstanceId] = brickSettingModel;
         return state;
      });
   }

   protected doSaveSpecificSettings(brickInstanceId: string, brickSetting: SingleCallTagEditorBrickSettingRemoteDto): Observable<any> {
      return this.brickSettingsService.changeSingleCallTagEditorBrickSettings({
         brickInstanceId: brickInstanceId,
         settings: brickSetting,
      });
   }
}
